import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import AsyncSelect from 'react-select/async';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';
import LoadingOverlay from '../LoadingOverlay';

function AjioStock() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const [totalORIBStock, setTotalORIBStock] = useState(0);
    const [totalPHXCStock, setTotalPHXCStock] = useState(0);
    const [totalPHXBStock, setTotalPHXBStock] = useState(0);
    const [totalIHYDStock, setTotalIHYDStock] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [branchName] = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        fetchTableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        let phxc = 0, phxb = 0, ihyd = 0;
        dataItem.forEach(item => { phxc += item.PHXCStock === -1 ? 0 : item.PHXCStock; phxb += item.ORIBandPHXBStock === -1 ? 0 : item.ORIBandPHXBStock; ihyd += item.IHYDStock === -1 ? 0 : item.IHYDStock; });
        setTotalPHXCStock(phxc); setTotalPHXBStock(phxb); setTotalIHYDStock(ihyd);
    }, [dataItem]);

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]); setLoading(true);
            const itid = (selectedOption && selectedOption.value !== null) ? selectedOption.value : 0;
            try {
                const response = await fetchData('ReportApi', `/AjioStock/${companyId}/${itid}`, token);
                if (response.responseCode === '1111') setDataItem(response.response_data);
            } catch (e) { console.error(e); } finally { setLoading(false); }
        }
    };

    const handleSearch = (e) => setSearchQuery(e.target.value);
    const filteredDataItem = dataItem.filter(item => Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase())));

    const fetchOptions = async (inputValue) => {
        if (inputValue.length <= 4) return [];
        const response = await fetchData('PurchaseApi', `/Item/${companyId}/${branchId}/${inputValue}`, token);
        if (response.responseCode === '1111') return response.response_data.map(item => ({ value: item.Id, label: item.Code + '-' + item.Name }));
        return [];
    };

    const handleChangeDrop = (opt) => setSelectedOption(opt);

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Ajio Stock');
        const rightAlignColumns = ['Purchase Rate','MRP','GST%','Inorbit Mall','PMC Chennai','Orion Mall & PMC Bangalore'];
        worksheet.mergeCells('A1:O1');
        const hc = worksheet.getCell('A1'); hc.value = `${branchName} Ajio Stock`; hc.font = { bold: true, size: 14 }; hc.alignment = { vertical: 'middle', horizontal: 'left' };
        const headerRowValues = ['Code','Item','Purchase Rate','MRP','GST%','HSN Code','PG','IG','ISG','PF','Category','Group','Inorbit Mall','PMC Chennai','Orion Mall & PMC Bangalore'];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell(cell => { cell.fill = { type:'pattern',pattern:'solid',fgColor:{argb:'FFB6D7A8'} }; cell.font = { bold:true,color:{argb:'FF000000'} }; cell.alignment = { vertical:'middle',horizontal:'center',wrapText:true }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));
        filteredDataItem.forEach(rowData => {
            const row = worksheet.addRow([parseFloat(rowData.Code),rowData.Name,parseFloat(rowData.PurchaseRate.toFixed(2)),parseFloat(rowData.MRP.toFixed(2)),parseFloat(rowData.GSTRate),rowData.HSNCode,rowData.PG,rowData.IG,rowData.ISG,rowData.PF,rowData.CategoryName,rowData.ItemGroupName,rowData.IHYDStock===-1?0:rowData.IHYDStock,rowData.PHXCStock===-1?0:rowData.PHXCStock,rowData.ORIBandPHXBStock===-1?0:rowData.ORIBandPHXBStock]);
            row.eachCell((cell, col) => { cell.alignment = { vertical:'middle',horizontal:rightAlignColumns.includes(headerRowValues[col-1])?'right':'center' }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
        });
        const totalRow = worksheet.addRow(['Total','','','','','','','','','','','',totalIHYDStock,totalPHXCStock,totalPHXBStock]);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, col) => { cell.alignment = { vertical:'middle',horizontal:rightAlignColumns.includes(headerRowValues[col-1])?'right':'center' }; cell.fill = { type:'pattern',pattern:'solid',fgColor:{argb:'FFFFF2CC'} }; cell.border = { top:{style:'thin'},left:{style:'thin'},bottom:{style:'thin'},right:{style:'thin'} }; });
        worksheet.views = [{ state: 'frozen', ySplit: 2 }];
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'Ajio Stock.xlsx');
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Ajio STOCK</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field" style={{ minWidth: 260 }}>
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>Item</label>
                            <div style={{ marginTop: 6 }}>
                                <AsyncSelect cacheOptions loadOptions={fetchOptions} onChange={handleChangeDrop} value={selectedOption} placeholder="Type to search items..." menuPortalTarget={document.body} menuPosition="fixed" styles={{ control: b => ({ ...b, minHeight: 31, height: 31, fontSize: 12.5, borderColor: 'rgba(200,212,224,0.7)', borderRadius: 6 }), valueContainer: b => ({ ...b, padding: '0 10px' }), indicatorsContainer: b => ({ ...b, height: 31 }), menuPortal: b => ({ ...b, zIndex: 9999 }) }} />
                            </div>
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchTableData} disabled={loading}><RefreshIcon /> {loading ? 'Loading...' : 'Refresh'}</button>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}><FeedIcon /> Excel</button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}><SearchIcon /> Search</button>
                    </div>
                    {showSearchInput && <input className="pg-input pg-search-input" placeholder="Search…" value={searchQuery} onChange={handleSearch} autoFocus style={{ marginTop: 6 }} />}
                </div>
            </div>
            {loading && <LoadingOverlay />}
            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead><tr><th>Code</th><th>Item</th><th>Purchase Rate</th><th>MRP</th><th>GST%</th><th>HSN Code</th><th>PG</th><th>IG</th><th>ISG</th><th>PF</th><th>Category</th><th>Group</th><th>Inorbit Mall</th><th>PMC Chennai</th><th>Orion Mall & PMC Bangalore</th></tr></thead>
                    <tbody>
                        {filteredDataItem.map((data, i) => (
                            <tr key={i}><td>{data.Code}</td><td>{data.Name}</td><td style={{ textAlign: 'right' }}>{data.PurchaseRate.toFixed(2)}</td><td style={{ textAlign: 'right' }}>{data.MRP.toFixed(2)}</td><td>{data.GSTRate}</td><td>{data.HSNCode}</td><td>{data.PG}</td><td>{data.IG}</td><td>{data.ISG}</td><td>{data.PF}</td><td>{data.CategoryName}</td><td>{data.ItemGroupName}</td><td>{data.IHYDStock===-1?0:data.IHYDStock}</td><td>{data.PHXCStock===-1?0:data.PHXCStock}</td><td>{data.ORIBandPHXBStock===-1?0:data.ORIBandPHXBStock}</td></tr>
                        ))}
                        <tr style={{ background: '#1c2a3a', color: '#fff', fontWeight: 700 }}>
                            <td>Total</td><td colSpan={11}></td><td>{totalIHYDStock}</td><td>{totalPHXCStock}</td><td>{totalPHXBStock}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default AjioStock;
